import React from 'react';
import {Box, Button} from '@mui/material';
import {FORM_REQUEST_STATUSES} from '../../../../../constants/formRequestStatuses';
import {CommunicationView, openViewer, ResponseView} from './SelfServiceForms';
/* eslint-disable react/prop-types */

// Maria, 18 October 2026: the look shared by the Forms tabs of student and staff profiles

// Maria, 18 October 2026: a centred hyphen in any column that does not apply to the form. 'Submitted By' and
// 'Submitted At' always apply, so they stay empty until the form is completed. The approval columns apply only to a
// form with approval stages, and stay empty on such a form until each stage is reached.
const NotApplicable = () => <span style={{display: 'block', width: '100%', textAlign: 'center'}}>-</span>;
const ALWAYS_APPLIES = ['editedBy', 'editedAt'];
const APPROVAL_COLUMNS = ['approvalStage', 'approvalStageProgress', 'approvalStatus', 'approvedBy', 'approvedAt', 'approvalNote'];
const hasApproval = row => APPROVAL_COLUMNS.some(key => row?.[key] !== null && row?.[key] !== undefined && row?.[key] !== '');
export const withHyphenWhenEmpty = columns => columns.map(column => {
    const key = column.id ?? column.accessorKey;
    return {
        ...column,
        Cell: props => {
            const value = props.cell.getValue();
            const empty = value === null || value === undefined || value === '';
            const applies = ALWAYS_APPLIES.includes(key) || (APPROVAL_COLUMNS.includes(key) && hasApproval(props.row.original));
            if (empty) return applies ? '' : <NotApplicable/>;
            return column.Cell ? column.Cell(props) : value;
        }
    };
});

// Maria, 18 October 2026: the Forms tables in the same style as Contacts: a light green header, shorter rows, a
// compact toolbar and no empty space below the table
// Maria, 18 October 2026: the open sub-tab filled in the table header's light green, so it is clear which one is open
export const SUB_TAB_SX = {
    borderRadius: '8px 8px 0 0',
    border: '1px solid transparent',
    borderBottom: 'none',
    // Maria, 18 October 2026: the open sub-tab outlined in green on its top and sides, like a real tab, with black words
    '&.Mui-selected': {color: '#000 !important', backgroundColor: '#e3f5f1', fontWeight: 700, border: '1px solid var(--tab-outline, #0A8572)', borderBottom: 'none'}
};

// Each column is as wide as its contents, and the toolbar icons are on the row of tabs above, so there is no blank
// band over the table.
export const FIT_TO_CONTENT = {width: 'auto !important', minWidth: 'auto !important', maxWidth: 'none !important'};
// Maria, 18 October 2026: the form name column wider than its contents need, so long names have room
// Maria, 20 October 2026: on a smaller screen the columns shrink before any text wraps, widest first (fitColumns below).
// A status tag and the buttons column stay on one line. The table only scrolls sideways when every column is already
// as narrow as its longest word.
const NO_WRAP_COLUMNS = ['status'];
const WRAP = {whiteSpace: 'normal', overflowWrap: 'normal', wordBreak: 'normal'};
// AD-2750: on a phone the status tag may take two lines, so the form name, the status and the buttons fit side by side
const PHONE_WRAP = {'@media (max-width:599.95px)': {whiteSpace: 'normal', '& span': {display: 'inline-block', lineHeight: 1.35}}};
const fitColumn = column => (NO_WRAP_COLUMNS.includes(column.id) ? {...FIT_TO_CONTENT, whiteSpace: 'nowrap', ...PHONE_WRAP} : FIT_TO_CONTENT);
// Maria, 19 October 2026: when a table scrolls sideways, the actions column (kept in view on the right) is widened
// just enough for its left edge to meet the start of a column, so it never covers half a heading.
const snapActionsColumn = container => {
    const table = container.querySelector('table');
    if (!table) return;
    // the width is kept on the container, which the table never redraws, and the actions cells read it
    container.style.removeProperty('--actions-min');
    const headings = [...table.querySelectorAll('thead th')];
    const actions = headings[headings.length - 1];
    if (!actions || container.scrollWidth <= container.clientWidth + 1) return;
    const actionsLeft = container.clientWidth - actions.offsetWidth;
    const covered = headings.slice(0, -1).find(th => th.offsetLeft < actionsLeft && th.offsetLeft + th.offsetWidth > actionsLeft);
    if (!covered) return;
    container.style.setProperty('--actions-min', `${actions.offsetWidth + actionsLeft - covered.offsetLeft}px`);
};

// Maria, 19 October 2026: where the actions column has no heading (Student Forms), the table's filter, columns and
// download icons sit in the heading row, at the right of the table
const alignToolbarWithActions = container => {
    const headings = container.querySelectorAll('thead th');
    const actions = headings[headings.length - 1];
    const paper = container.closest('.MuiPaper-root');
    const firstIcon = paper?.querySelector('svg[data-testid="FilterListRoundedIcon"]')?.closest('button');
    if (!actions || !firstIcon || actions.innerText.trim() !== '') return;
    // the icons' bar is the nearest ancestor placed absolutely in the table
    let toolbar = firstIcon.parentElement;
    while (toolbar && toolbar !== paper && getComputedStyle(toolbar).position !== 'absolute') toolbar = toolbar.parentElement;
    if (!toolbar || toolbar === paper) return;
    // Maria, 19 October 2026: the icons end at the right of the table
    const icons = [...firstIcon.parentElement.parentElement.querySelectorAll('button')];
    const lastIcon = icons[icons.length - 1] ?? firstIcon;
    // the last icon's drawing (not its round button) ends 12px in from the table's right-hand edge.
    // Maria, 19 October 2026: in the narrow column on Forms the icons are centred over the buttons
    const cell = actions.getBoundingClientRect();
    const first = firstIcon.getBoundingClientRect();
    const last = lastIcon.getBoundingClientRect();
    const shift = actions.dataset.iconsColumn
        ? (cell.left + cell.width / 2) - (first.left + last.right) / 2
        : (cell.right - 12) - (lastIcon.querySelector('svg') ?? lastIcon).getBoundingClientRect().right;
    // and centred on the heading row
    const heading = actions.getBoundingClientRect();
    const icon = firstIcon.getBoundingClientRect();
    const drop = (heading.top + heading.height / 2) - (icon.top + icon.height / 2);
    if (Math.abs(shift) < 1 && Math.abs(drop) < 1) return;
    const parent = toolbar.offsetParent?.getBoundingClientRect() ?? {left: 0, top: 0};
    toolbar.style.right = 'auto';
    toolbar.style.left = `${Math.round(toolbar.getBoundingClientRect().left - parent.left + shift)}px`;
    toolbar.style.top = `${Math.round(toolbar.getBoundingClientRect().top - parent.top + drop)}px`;
};

const ACTIONS_MIN = {boxSizing: 'border-box', minWidth: 'var(--actions-min, auto) !important'};

// Maria, 20 October 2026: when the table is wider than the page, the widest columns are brought down to the same width,
// so they wrap first, until the table fits. The last column (the buttons) keeps its width. Columns are never narrower
// than their longest word.
export const capColumnWidths = (max, min, available) => {
    const total = max.reduce((sum, width) => sum + width, 0);
    if (total <= available) return null;
    const widths = cap => max.map((width, i) => Math.max(min[i], Math.min(width, cap)));
    let low = 0;
    let high = Math.max(...max);
    for (let step = 0; step < 30; step++) {
        const cap = (low + high) / 2;
        if (widths(cap).reduce((sum, width) => sum + width, 0) > available) high = cap; else low = cap;
    }
    // a column left at its full width keeps every pixel of it, so its text is not cut short
    return widths(low).map((width, i) => (width >= max[i] - 0.5 ? Math.ceil(max[i]) : Math.floor(width)));
};

const PHONE_WIDTH = 600;

export const scrollsAcross = (flag, width) => flag === 'on' && width >= PHONE_WIDTH;

const fitColumns = container => {
    const table = container.querySelector('table');
    const heads = table ? [...table.querySelectorAll('thead th')] : [];
    if (heads.length < 3) return;
    heads.forEach(th => ['width', 'min-width', 'max-width', 'box-sizing'].forEach(name => th.style.removeProperty(name)));
    // Maria, 23 October 2026: on the Forms page the columns keep their own width and the table scrolls across, except on a
    // phone
    if (scrollsAcross(container.dataset.scrollAcross, container.clientWidth)) return;
    // the buttons column is measured at its own width, before snapActionsColumn widens it for a table that scrolls
    container.style.removeProperty('--actions-min');
    table.dataset.fit = 'max';
    const max = heads.map(th => th.getBoundingClientRect().width);
    table.dataset.fit = 'min';
    const min = heads.map(th => th.getBoundingClientRect().width);
    delete table.dataset.fit;
    const buttons = max[max.length - 1];
    const available = container.clientWidth - buttons - heads.length - 2;
    let widths = capColumnWidths(max.slice(0, -1), min.slice(0, -1), available);
    if (!widths) return;
    // AD-2756: on a list with tick boxes (the Forms page), when the table cannot fit even with every column at its
    // narrowest, it scrolls anyway, so the columns after the form name and the groups stay on one line rather than wrap
    // word by word
    const ticks = !!heads[0].querySelector('input[type="checkbox"]');
    const narrowest = min.slice(0, -1).reduce((sum, width) => sum + width, 0);
    if (ticks && narrowest > available) {
        // the form name and the groups have up to 220px each, so they wrap onto a few lines, not one word per line
        widths = widths.map((width, i) => (i > 2 ? Math.ceil(max[i]) : (i > 0 ? Math.max(width, Math.min(Math.ceil(max[i]), 220)) : width)));
    }
    // AD-2750 (David, 22 October 2026): on a phone the table scrolls sideways anyway, so the form name is given the room
    // left beside the second column (the status, at its narrowest) and the buttons, and no longer wraps one word per line
    // AD-2756: on a list with tick boxes (the Forms page) the form name is the column after the tick boxes, and it is
    // given all the room beside them, as the other columns are reached by scrolling
    if (container.clientWidth < PHONE_WIDTH) {
        const name = ticks ? 1 : 0;
        const beside = ticks ? widths[0] : (widths[1] ?? 0);
        widths[name] = Math.max(widths[name], Math.min(Math.ceil(max[name]), Math.floor(available - beside)));
    }
    widths.forEach((width, i) => {
        heads[i].style.setProperty('box-sizing', 'border-box', 'important');
        ['width', 'min-width', 'max-width'].forEach(name => heads[i].style.setProperty(name, `${width}px`, 'important'));
    });
};

// the table hands its container to 'ref.current'; each container is watched once
const actionsSnapRef = {
    set current(container) {
        if (!container || container.dataset.actionsSnap || typeof ResizeObserver === 'undefined') return;
        container.dataset.actionsSnap = 'on';
        let frame = 0;
        let fittedWidth = -1;
        let contentChanged = true;
        const run = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                // the columns are worked out again only when the page width or the rows change, as measuring them
                // changes the table's height and would otherwise start this again
                if (contentChanged || container.clientWidth !== fittedWidth) {
                    contentChanged = false;
                    fittedWidth = container.clientWidth;
                    fitColumns(container);
                }
                snapActionsColumn(container);
                alignToolbarWithActions(container);
            });
        };
        new ResizeObserver(run).observe(container);
        new MutationObserver(() => {
            contentChanged = true;
            run();
        }).observe(container, {childList: true, subtree: true, characterData: true});
        run();
    },
    get current() {
        return null;
    }
};

export const COMPACT_TABLE_PROPS = {
    muiTableContainerProps: {ref: actionsSnapRef},
    enableColumnResizing: false,
    muiTopToolbarProps: {sx: {position: 'absolute', top: 4, right: 8, zIndex: 4, width: 'auto', minHeight: '0 !important', p: 0, backgroundColor: 'transparent', '& > .MuiBox-root': {p: 0, minHeight: 0}}},
    muiTableProps: {
        sx: {
            tableLayout: 'auto',
            width: '100%',
            // fitColumns measures each column on one line ('max') and at its narrowest ('min')
            '&[data-fit="max"]': {width: 'max-content !important', '& th, & td, & .Mui-TableHeadCell-Content-Wrapper': {whiteSpace: 'nowrap !important'}},
            // a heading is never narrower than its longest word, so a word is not cut short with '...'
            '&[data-fit="min"]': {width: '0 !important', minWidth: '0 !important', '& .Mui-TableHeadCell-Content-Wrapper': {overflow: 'visible !important', minWidth: 'min-content !important'}},
            '& .Mui-TableHeadCell-Content-Wrapper': {minWidth: 'min-content'}
        }
    },
    muiTableHeadCellProps: ({column}) => ({sx: {'&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1}, ...fitColumn(column), fontWeight: 600, py: 1, lineHeight: 1.25, ...WRAP, verticalAlign: 'middle', '& .Mui-TableHeadCell-Content': {alignItems: 'center'}, '& .Mui-TableHeadCell-Content-Labels': {alignItems: 'center'}, '& .Mui-TableHeadCell-Content-Wrapper': WRAP}}),
    muiTableBodyCellProps: ({column}) => ({sx: {py: 0.5, verticalAlign: 'middle', ...WRAP, ...fitColumn(column)}}),
    muiTableBodyProps: {sx: {'& :last-child td': {borderBottom: 0}}}
};

// AD-2735: on a phone the row of tabs has no room for the table's icons, which then covered the Communications tab. On
// a small screen the icons go back above the table, as before the AD-2725 layout.
export const withToolbarAboveOnSmallScreen = (props, isSmallScreen) => (isSmallScreen
    ? {
        ...props,
        muiTopToolbarProps: {
            sx: {
                ...props.muiTopToolbarProps?.sx,
                position: 'static',
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                py: 0.5
            }
        }
    }
    : props);

// AD-2734: a table inside a flex box is as tall as its rows. Without this its container shares out the box height and
// shrinks to nothing on screens bigger than a phone.
export const ROWS_HEIGHT_TABLE_PROPS = {
    muiTablePaperProps: {sx: {display: 'block', flexGrow: 0, height: 'auto'}},
    muiTableContainerProps: {...COMPACT_TABLE_PROPS.muiTableContainerProps, sx: {flex: '0 0 auto'}}
};

// Maria, 18 October 2026: a clear button at the end of each row. Maria, 19 October 2026: 'Open' while the form is
// waiting and 'View' once it has been submitted, both in the same size of box. Download and Print are in the form's
// view, so they are not on the row. Clicking anywhere on the row still opens the form.
// 'canEdit' marks a submitted response its member of staff may edit (AD-2695), which also has 'Edit response'.
const ROW_BUTTON_SX = {width: 76, minWidth: 76, px: 1.25, py: 0.25, fontSize: 13, whiteSpace: 'nowrap', boxShadow: 'none'};
// Maria, 19 October 2026: 'View' the same as 'Open', in green
const VIEW_BUTTON_SX = {...ROW_BUTTON_SX, backgroundColor: '#0DAD95', '&:hover': {backgroundColor: '#0A8572', boxShadow: 'none'}};

// the column is as wide as its buttons: one button, or two where a response can be edited
// 'claim' (Student Forms on a staff profile) gives a form that has to be claimed a 'Claim' button until it is claimed.
// Once this member of staff has claimed it, 'Open' and 'Unclaim' are shown. A form claimed by someone else is not in
// the list (FormRequestQuery.GetAllPaginatedByEmployeeId).
// Maria, 19 October 2026: on Student Forms the filter, columns and download icons sit in this heading, on the right,
// after 'Actions', so the heading leaves room for them
// Maria, 19 October 2026: only as wide as the buttons, or the three icons in its heading
// Maria, 19 October 2026: a little wider when needed, so its left edge meets the start of a column
const ICONS_WIDTH = 'max(140px, var(--actions-min, 0px)) !important';
const ICONS_COLUMN = {width: ICONS_WIDTH, minWidth: ICONS_WIDTH, maxWidth: ICONS_WIDTH, px: 1, textAlign: 'center', '& > .MuiBox-root': {justifyContent: 'center'}};

// AD-2750 (David, 22 October 2026): on a phone the buttons column is only as wide as its buttons plus the normal cell
// padding; it only widens to cover a heading it would otherwise cut in half. Tablet and desktop as before.
const COMPACT_ACTIONS = {...FIT_TO_CONTENT, ...ACTIONS_MIN, px: 1};
export const actionsColumnSx = ({iconsInHeading, compact}) => (compact
    ? COMPACT_ACTIONS
    : {...FIT_TO_CONTENT, ...ACTIONS_MIN, px: 3, ...(iconsInHeading ? ICONS_COLUMN : {})});

// 'iconsInHeading': no 'Actions' heading, as the table's icons go in its place
// 'compact': a phone, where the table's icons are above the table (withToolbarAboveOnSmallScreen)
export const formRowButtons = ({open, canEdit = () => false, edit, claim, iconsInHeading, compact = false}) => ({
    // Maria, 19 October 2026: 'Actions' on every Forms list, with the icons on the row of tabs above
    header: iconsInHeading && !compact ? '' : 'Actions',
    size: edit || claim ? 205 : 104,
    grow: false,
    muiTableBodyCellProps: {sx: {...actionsColumnSx({iconsInHeading, compact}), '&.MuiTableCell-root': {backgroundColor: '#fff', backgroundImage: 'none', opacity: 1, borderLeft: '1px solid rgba(0, 0, 0, 0.12)'}}},
    // Maria, 19 October 2026: the icons centred over the buttons on Student Forms and Staff Forms too
    muiTableHeadCellProps: {...(iconsInHeading && !compact ? {'data-icons-column': 'on'} : {}), sx: {...actionsColumnSx({iconsInHeading, compact}), '&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1, borderLeft: '1px solid rgba(0, 0, 0, 0.12)', fontWeight: 600}, py: 1, lineHeight: 1.25, verticalAlign: 'middle', textAlign: 'left', '& .Mui-TableHeadCell-Content': {justifyContent: 'flex-start'}}},
    Cell: ({row}) => {
        const request = row.original;
        // AD-2754: on a phone two buttons sit one above the other, so the form name and status keep their room
        const twoButtons = compact ? {flexDirection: 'column', alignItems: 'flex-start', gap: 0.75} : {};
        const onClick = action => event => {
            event.stopPropagation();
            action(request);
        };
        if (request.status === FORM_REQUEST_STATUSES.submitted) {
            return (
                <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-start', ...twoButtons}}>
                    {canEdit(request) && <Button size="small" variant="outlined" onClick={onClick(edit)} sx={ROW_BUTTON_SX}>Edit response</Button>}
                    <ResponseView request={request} buttonSx={VIEW_BUTTON_SX}/>
                </Box>
            );
        }
        // AD-2759: on a phone an expired form has no button, as there is no submission to show
        if (request.status === FORM_REQUEST_STATUSES.expired) {
            if (compact) return null;
            return <Box sx={{display: 'flex', justifyContent: 'flex-start'}}><Button size="small" variant="contained" onClick={onClick(open)} sx={VIEW_BUTTON_SX}>View</Button></Box>;
        }
        if (claim?.needsClaim(request)) {
            if (claim.claimedByMe(request)) {
                return (
                    <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-start', ...twoButtons}}>
                        <Button size="small" variant="contained" onClick={onClick(open)} data-testid="complete-form" sx={ROW_BUTTON_SX}>Open</Button>
                        <Button size="small" variant="outlined" onClick={onClick(claim.unclaim)} disabled={claim.busy} sx={ROW_BUTTON_SX}>Unclaim</Button>
                    </Box>
                );
            }
            // Maria, 19 October 2026: 'Open' on every form not yet submitted, with 'Claim' beside it until it is claimed
            return (
                <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-start', ...twoButtons}}>
                    <Button size="small" variant="contained" onClick={onClick(open)} data-testid="complete-form" sx={ROW_BUTTON_SX}>Open</Button>
                    <Button size="small" variant="outlined" onClick={onClick(claim.claim)} disabled={claim.busy} sx={ROW_BUTTON_SX}>Claim</Button>
                </Box>
            );
        }
        return (
            <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                <Button size="small" variant="contained" onClick={onClick(open)} data-testid="complete-form" sx={ROW_BUTTON_SX}>Open</Button>
            </Box>
        );
    }
});

// Maria, 18 October 2026: 'View' at the end of each communication in a profile, opening the communication in the viewer
// with 'Download' and 'Print'. A communication cannot be deleted from a profile.
export const communicationRowButtons = () => ({
    // Maria, 19 October 2026: 'Actions', as on the other Forms lists
    header: 'Actions',
    size: 90,
    // Maria, 19 October 2026: 'Actions' centred over the View buttons
    muiTableHeadCellProps: {align: 'center', sx: {'&.MuiTableCell-root': {backgroundColor: '#e3f5f1', backgroundImage: 'none', opacity: 1, fontWeight: 600}, py: 1, lineHeight: 1.25, verticalAlign: 'middle', '& .Mui-TableHeadCell-Content': {justifyContent: 'center'}}},
    muiTableBodyCellProps: {align: 'center', sx: {'&.MuiTableCell-root': {backgroundColor: '#fff', backgroundImage: 'none', opacity: 1}}},
    Cell: ({row}) => (
        <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <CommunicationView communication={row.original}/>
        </Box>
    )
});

// AD-2759 (David, 23 October 2026; Maria: phones only): tapping a row on a profile's Forms lists. On a phone a submitted
// form opens on the full page and an expired form does nothing. Otherwise the row opens as before (openForm).
export const openFormRow = (request, openForm, isPhone) => {
    if (isPhone && request.status === FORM_REQUEST_STATUSES.expired) return;
    if (isPhone && request.status === FORM_REQUEST_STATUSES.submitted && openViewer(`request-${request.id}`)) return;
    openForm(request);
};

// AD-2759: tapping a communication's row. On a phone it opens on the full page; otherwise as before (openPage).
export const openCommunicationRow = (communication, openPage, isPhone) => {
    if (isPhone && openViewer(`communication-${communication.requestId}`)) return;
    openPage(communication);
};
